import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaFacebookF, FaInstagram, FaYoutube, FaHeart, FaLock } from 'react-icons/fa';
import '../css/navbar.css';
import logo from '../assets/ngoLogo.webp';
import * as contentService from '../services/contentService';

const DEFAULT_PHONE = "+91-9603040740";
const DEFAULT_EMAIL = "lovingarmsorphanage@gmail.com";
const DEFAULT_NAV = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Gallery", href: "/gallery" },
    { name: "Construction", href: "/construction" },
    { name: "Team", href: "/team" },
    { name: "Contact", href: "/contact" },
];

export default function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [settings, setSettings] = useState(null);
    const [links, setLinks] = useState(DEFAULT_NAV);
    const path = useLocation().pathname;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMobileOpen(false);

        const handleScroll = () => {
            if (window.scrollY > 40) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Load live dynamic content with fallback
        const loadNavData = async () => {
            try {
                const s = await contentService.getSiteSettings();
                if (s) setSettings(s);

                const n = await contentService.getNavigationLinks();
                if (n && n.length > 0) {
                    const visible = n.filter(item => item.visible !== false);
                    if (visible.length > 0) setLinks(visible);
                }
            } catch (err) {
                console.error("Failed to fetch navbar dynamic content:", err);
            }
        };

        loadNavData();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [path]);

    const phoneNum = settings?.phone || DEFAULT_PHONE;
    const email = settings?.email || DEFAULT_EMAIL;
    const facebookUrl = settings?.socialLinks?.facebook || "https://www.facebook.com/Vekatasrikanth";
    const instagramUrl = settings?.socialLinks?.instagram || "https://www.instagram.com/lovingarms_orphanage/?hl=en";
    const youtubeUrl = settings?.socialLinks?.youtube || "https://www.youtube.com/";

    // Filter out any donate link from the links array so it is never displayed twice
    const menuLinks = links.filter(item => 
        item.href !== '/donate' && 
        item.name?.trim().toLowerCase() !== 'donate'
    );

    return (
        <header className="modern-header-wrapper">
            {/* Top Announcement & Quick Contact Bar */}
            <div className={`modern-topbar ${scrolled ? 'topbar-hidden' : ''}`}>
                <div className="container">
                    <div className="topbar-inner">
                        <div className="topbar-contacts">
                            <a href={`tel:${phoneNum}`} className="topbar-item">
                                <FaPhoneAlt className="me-1" />
                                <span>{phoneNum}</span>
                            </a>
                            <span className="topbar-divider">|</span>
                            <a href={`mailto:${email}`} className="topbar-item">
                                <FaEnvelope className="me-1" />
                                <span>{email}</span>
                            </a>
                        </div>
                        <div className="topbar-right-actions">
                            <div className="topbar-socials">
                                <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                    <FaFacebookF />
                                </a>
                                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                    <FaInstagram />
                                </a>
                                <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                    <FaYoutube />
                                </a>
                            </div>
                            <span className="topbar-divider">|</span>
                            <Link to="/admin/dashboard" className="topbar-admin-btn" title="Admin Portal">
                                <FaLock className="me-1" /> Admin
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Navigation Bar */}
            <nav className={`modern-navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
                <div className="container">
                    <div className="navbar-content">
                        {/* Brand Logo */}
                        <Link to="/" className="navbar-brand-modern">
                            <img src={logo} alt="Loving Arms Orphanage Logo" className="brand-logo-img" />
                            <div className="brand-text-block">
                                <span className="brand-title">{settings?.siteName || "Loving Arms"}</span>
                                <span className="brand-subtitle">{settings?.tagline || "Orphanage"}</span>
                            </div>
                        </Link>

                        {/* Desktop Navigation Links */}
                        <div className="navbar-desktop-menu">
                            {menuLinks.map((item) => (
                                <Link
                                    key={item.href || item.name}
                                    to={item.href}
                                    className={`nav-link-modern ${path === item.href ? 'nav-link-active' : ''}`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                            <Link to="/donate" className="btn-donate-modern">
                                <FaHeart className="me-2 text-danger" /> Donate
                            </Link>
                        </div>

                        {/* Mobile Actions: Always-Visible Quick Donate + Hamburger Toggle */}
                        <div className="mobile-header-actions">
                            <Link to="/donate" className="mobile-header-donate-btn">
                                <FaHeart className="me-1 text-danger" /> Donate
                            </Link>
                            <button
                                type="button"
                                className={`mobile-toggle-btn ${mobileOpen ? 'toggle-active' : ''}`}
                                onClick={() => setMobileOpen(!mobileOpen)}
                                aria-label="Toggle navigation"
                            >
                                <span className="toggle-bar"></span>
                                <span className="toggle-bar"></span>
                                <span className="toggle-bar"></span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Drawer */}
                <div className={`mobile-drawer ${mobileOpen ? 'drawer-open' : ''}`}>
                    <div className="mobile-drawer-links">
                        {menuLinks.map((item) => (
                            <Link
                                key={item.href || item.name}
                                to={item.href}
                                onClick={() => setMobileOpen(false)}
                                className={`mobile-nav-link ${path === item.href ? 'mobile-nav-active' : ''}`}
                            >
                                {item.name}
                            </Link>
                        ))}

                        <div className="mobile-drawer-contacts">
                            <a href={`tel:${phoneNum}`}>
                                <FaPhoneAlt className="me-2" />{phoneNum}
                            </a>
                            <a href={`mailto:${email}`}>
                                <FaEnvelope className="me-2" />{email}
                            </a>
                            <Link to="/admin/dashboard" onClick={() => setMobileOpen(false)} className="mobile-drawer-admin-link">
                                <FaLock className="me-2 text-warning" /> Admin Portal
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
}